import React from 'react';
import {
  Box,
  Button,
  ButtonIcon,
  Chip,
  Icon,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  CF_NEU_BLD,
  CF_NEU_STL,
  CF_SEC_BLD,
} from '../../../../shared/constants';
import { useInvertedLocalize } from '../../../../shared/hooks';
import { type TransactionItem } from '../AuditLogViewerAuditorLeftBody/types';

import './AuditLogViewerAuditorTransactionSidePanel.css';

export interface AuditLogViewerAuditorTransactionSidePanelProps {
  /** Additional CSS class name */
  className?: string;
  /** Transaction item data to display */
  item: TransactionItem;
  /** Callback when panel is closed */
  onClose?: () => void;
  /** Callback when View Logs is clicked */
  onViewLogs?: () => void;
}

/**
 * AuditLogViewerAuditorTransactionSidePanel Component
 * Side panel that displays transaction details for the auditor view.
 * Shows Entitlement information, criticality levels, usage stats, and actions.
 */
function AuditLogViewerAuditorTransactionSidePanel({
  className,
  item,
  onClose,
  onViewLogs,
}: AuditLogViewerAuditorTransactionSidePanelProps): React.ReactElement {
  const l = useInvertedLocalize();

  return (
    <Box className={cx('AuditLogViewerAuditorTransactionSidePanel', className)}>
      {/* Header Section */}
      <Box className='AuditLogViewerAuditorTransactionSidePanel-header'>
        <Box className='AuditLogViewerAuditorTransactionSidePanel-headerContent'>
          {/* Application Icon */}
          <Box className='AuditLogViewerAuditorTransactionSidePanel-appIcon'>
            <Icon kind='Entitlement' size='medium' />
          </Box>

          {/* Title and Description */}
          <Box className='AuditLogViewerAuditorTransactionSidePanel-description'>
            <Typography lineLimit={3} kind='h3' color={CF_NEU_BLD}>
              {item.name}
            </Typography>
            <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
              {l('Transaction Code Description')}
            </Typography>
          </Box>
        </Box>

        {/* Close Button */}
        <ButtonIcon
          size='medium'
          kind='ghost'
          ariaLabel={l('close')}
          icon={<Icon kind='Close' size='medium' />}
          onClick={onClose}
        />
      </Box>

      {/* Content Section */}
      <Box className='AuditLogViewerAuditorTransactionSidePanel-content'>
        <Box className='AuditLogViewerAuditorTransactionSidePanel-section'>
          {/* Entitlement */}
          <Box className='AuditLogViewerAuditorTransactionSidePanel-field'>
            <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
              {l('Entitlement')}
            </Typography>
            <Typography lineLimit={3} kind='body2' color={CF_SEC_BLD}>
              {item.tCode}
            </Typography>
          </Box>

          {/* Description */}
          <Box className='AuditLogViewerAuditorTransactionSidePanel-field'>
            <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
              {l('Description')}
            </Typography>
            <Typography lineLimit={3} kind='body2' color={CF_SEC_BLD}>
              {item.name}
            </Typography>
          </Box>

          {/* Function Area */}
          <Box className='AuditLogViewerAuditorTransactionSidePanel-field'>
            <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
              {l('Function Area')}
            </Typography>
            <Typography lineLimit={3} kind='body2' color={CF_SEC_BLD}>
              {l('Role Maintenance')}
            </Typography>
          </Box>

          {/* Entitlement Deviation */}
          <Box className='AuditLogViewerAuditorTransactionSidePanel-field'>
            <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
              {l('Entitlement Deviation')}
            </Typography>
            <Chip
              color='critical'
              size='small'
              kind='outlined'
              label={l('Yes')}
            />
          </Box>

          {/* SOX Critical */}
          <Box className='AuditLogViewerAuditorTransactionSidePanel-field'>
            <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
              {l('SOX Critical')}
            </Typography>
            <Typography lineLimit={3} kind='body2' color={CF_SEC_BLD}>
              {l('High')}
            </Typography>
          </Box>

          {/* Sys Critical */}
          <Box className='AuditLogViewerAuditorTransactionSidePanel-field'>
            <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
              {l('Sys Critical')}
            </Typography>
            <Typography lineLimit={3} kind='body2' color={CF_SEC_BLD}>
              {l('High')}
            </Typography>
          </Box>

          {/* Usage Count */}
          <Box className='AuditLogViewerAuditorTransactionSidePanel-field'>
            <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
              {l('Usage Count')}
            </Typography>
            <Typography lineLimit={3} kind='body2' color={CF_SEC_BLD}>
              {item.usageCount}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Footer Action Bar */}
      <Box className='AuditLogViewerAuditorTransactionSidePanel-footer'>
        <Box className='AuditLogViewerAuditorTransactionSidePanel-footerActions'>
          <Button
            kind='filled'
            size='medium'
            rightIcon={<Icon kind='ArrowRight' size='small' />}
            onClick={onViewLogs}>
            {l('View Logs')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

AuditLogViewerAuditorTransactionSidePanel.defaultProps = {
  className: undefined,
  onClose: undefined,
  onViewLogs: undefined,
};

export default AuditLogViewerAuditorTransactionSidePanel;
